import React, {useMemo, useState} from "react"
import PropTypes from 'prop-types'
import {faCopy, faDownload, faUndo} from '@fortawesome/free-solid-svg-icons'

import ToolBar from '../../../UI/ToolBar/ToolBar'
import Buttons from '../../../UI/ToolBar/Buttons'
import {toMarkdownTable, toHtmlTable, createEmptyGrid} from '../../../utils/tableFormatters'
import styles from './TableGenerator.module.scss'

const DEFAULT_MAX_ROWS = 10
const DEFAULT_MAX_COLUMNS = 15

TableGenerator.propTypes = {
    maxRows: PropTypes.number,
    maxColumns: PropTypes.number,
}

export default function TableGenerator({maxRows = DEFAULT_MAX_ROWS, maxColumns = DEFAULT_MAX_COLUMNS}) {
    const [dimensions, setDimensions] = useState(null)
    const [hoverCell, setHoverCell] = useState({row: 0, column: 0})
    const [grid, setGrid] = useState([])
    const [hasHeader, setHasHeader] = useState(true)
    const [format, setFormat] = useState('markdown')
    const [copied, setCopied] = useState(false)

    const output = useMemo(() => {
        if (!grid.length) return ''
        return format === 'markdown' ? toMarkdownTable(grid, {hasHeader}) : toHtmlTable(grid, {hasHeader})
    }, [grid, hasHeader, format])

    const confirmDimensions = (rows, columns) => {
        setDimensions({rows, columns})
        setGrid(createEmptyGrid(rows, columns))
    }

    const handleCellChange = (rowIndex, columnIndex, value) => {
        setGrid(current => {
            const next = current.map(row => [...row])
            next[rowIndex][columnIndex] = value
            return next
        })
    }

    const handleReset = () => {
        setDimensions(null)
        setGrid([])
        setHoverCell({row: 0, column: 0})
    }

    const handleCopy = () => {
        if (!output) return
        navigator.clipboard.writeText(output)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    const handleDownload = () => {
        if (!output) return
        const extension = format === 'markdown' ? 'md' : 'html'
        const blob = new Blob([output], {type: 'text/plain'})
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `table.${extension}`
        link.click()
        URL.revokeObjectURL(url)
    }

    if (!dimensions) {
        return (
            <div className={styles.TableGenerator}>
                <h1>Table Generator</h1>
                <div className={styles.pickerLabel}>{hoverCell.row + 1} &times; {hoverCell.column + 1}</div>
                <table className={styles.picker} onMouseLeave={() => setHoverCell({row: 0, column: 0})}>
                    <tbody>
                    {Array.from({length: maxRows}, (item, row) => (
                        <tr key={row}>
                            {Array.from({length: maxColumns}, (item, column) => (
                                <td
                                    key={column}
                                    className={row <= hoverCell.row && column <= hoverCell.column ? styles.selected : ''}
                                    onMouseOver={() => setHoverCell({row, column})}
                                    onClick={() => confirmDimensions(row + 1, column + 1)}
                                />
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        )
    }

    const headerButtons = [
        {text: 'Header row', clickHandler: () => setHasHeader(h => !h), active: hasHeader},
    ]

    const formatButtons = [
        {text: 'Markdown', clickHandler: () => setFormat('markdown'), active: format === 'markdown'},
        {text: 'HTML', clickHandler: () => setFormat('html'), active: format === 'html'},
    ]

    const outputActionButtons = [
        {tip: copied ? 'Copied!' : 'Copy to clipboard', clickHandler: handleCopy, icon: faCopy},
        {tip: 'Download file', clickHandler: handleDownload, icon: faDownload},
    ]

    const resetButtons = [
        {tip: 'Pick a new size', clickHandler: handleReset, icon: faUndo},
    ]

    return (
        <div className={styles.TableGenerator}>
            <ToolBar>
                <div className={styles.paneToolbar}>
                    <Buttons buttons={resetButtons}/>
                    <Buttons buttons={headerButtons}/>
                    <Buttons buttons={formatButtons}/>
                    <div className={styles.paneToolbar} style={{justifyContent: 'flex-end'}}>
                        <Buttons buttons={outputActionButtons}/>
                    </div>
                </div>
            </ToolBar>
            <div className={styles.editorArea}>
                <table className={styles.editableTable}>
                    <tbody>
                    {grid.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {row.map((value, columnIndex) => (
                                <td key={columnIndex}>
                                    <input
                                        value={value}
                                        onChange={e => handleCellChange(rowIndex, columnIndex, e.currentTarget.value)}
                                        className={hasHeader && rowIndex === 0 ? styles.headerCell : ''}
                                    />
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>
                <textarea className={styles.output} value={output} readOnly/>
            </div>
        </div>
    )
}
